import React from "react";
import SearchableDropdown from "../SearchableDropdown";

export interface SelectOption {
 value: string | number;
 label: string;
 disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "onChange"> {
 label?: string;
 error?: string;
 helperText?: string;
 options: SelectOption[];
 placeholder?: string;
 fullWidth?: boolean;
 searchable?: boolean; // Allow typing to search/filter options (default: true)
 onChange?: (e: { target: { value: string } }) => void;
}

export const Select: React.FC<SelectProps> = ({
 label,
 error,
 helperText,
 options,
 placeholder,
 fullWidth = false,
 searchable = true,
 className = "",
 id,
 value,
 onChange,
 disabled,
 multiple,
 ...props
}) => {
 const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
 const hasError = !!error;
 
 // Convert value to string for SearchableDropdown
 const stringValue = value !== undefined && value !== null ? String(value) : "";
 
 // Create a stable getDisplayLabel function that includes options in its closure
 // This ensures it always has access to the latest options
 // Must be called before any conditional returns (React Hooks rule)
 const getDisplayLabelFunc = React.useCallback((val: string) => {
  // First try to find the option (even for empty strings, as empty string can be a valid option value)
  const option = options.find((opt) => String(opt.value) === val);
  if (option) {
   return option.label;
  }
  // If no option found and value is empty/undefined, return placeholder
  if (!val) {
   return placeholder || "";
  }
  // Fallback to the value itself if option not found
  return val;
 }, [options, placeholder]);
 
 // Create a key that changes when options change to force SearchableDropdown to remount
 // This ensures it re-evaluates the display label when options are loaded
 // Use length and a simple hash of option values for performance
 const optionsKey = `${options.length}-${options.slice(0, 3).map(opt => String(opt.value)).join('-')}`;

 // For multi-select, fall back to native select (SearchableDropdown doesn't support multi-select yet)
 if (multiple) {
  const selectClasses = `
   block w-full px-3 py-2 border  bg-white
   focus:outline-none focus:ring-2 focus:ring-store-secondary focus:border-store-secondary
   disabled:bg-gray-100 disabled:cursor-not-allowed
   ${hasError ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-store-accent-light"}
   ${fullWidth ? "w-full" : ""}
   ${className}
  `.trim().replace(/\s+/g, " ");

  return (
   <div className={fullWidth ? "w-full" : ""}>
    {label && (
     <label
      htmlFor={selectId}
      className="block text-sm font-medium text-gray-700 mb-1"
     >
      {label}
     </label>
    )}
    <select
     id={selectId}
     className={selectClasses}
     aria-invalid={hasError}
     aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
     value={value}
     onChange={onChange as any}
     disabled={disabled}
     multiple={multiple}
     {...props}
    >
     {placeholder && (
      <option value="" disabled>
       {placeholder}
      </option>
     )}
     {options.map((option) => (
      <option
       key={option.value}
       value={option.value}
       disabled={option.disabled}
      >
       {option.label}
      </option>
     ))}
    </select>
    {error && (
     <p id={`${selectId}-error`} className="mt-1 text-sm text-red-600" role="alert">
      {error}
     </p>
    )}
    {helperText && !error && (
     <p id={`${selectId}-helper`} className="mt-1 text-sm text-gray-500">
      {helperText}
     </p>
    )}
   </div>
  );
 }

 return (
  <div className={`${fullWidth ? "w-full" : ""} ${className}`}>
   {label && (
    <label
     htmlFor={selectId}
     className="block text-sm font-medium text-gray-700 mb-1"
    >
     {label}
    </label>
   )}
   <div className={hasError ? "border-red-500" : ""}>
    <SearchableDropdown
     key={`${selectId}-${optionsKey}`}
     fetchOptions={async (search: string) => {
      const filtered = options
       .filter((opt) => !opt.disabled)
       .filter((opt) =>
        searchable
         ? opt.label.toLowerCase().includes(search.toLowerCase())
         : true // Don't filter when searchable is false
       )
       .map((opt) => ({
        _id: String(opt.value),
        label: opt.label,
       }));
      
      // Add placeholder option if provided and no value selected
      if (placeholder && !stringValue) {
       return [{ _id: "", label: placeholder }, ...filtered];
      }
      return filtered;
     }}
     placeholder={placeholder || "Select an option..."}
     value={stringValue}
     getDisplayLabel={getDisplayLabelFunc}
     searchable={searchable}
     onSelect={(option) => {
      if (onChange) {
       onChange({ target: { value: option._id } } as any);
      }
     }}
    />
   </div>
   {error && (
    <p id={`${selectId}-error`} className="mt-1 text-sm text-red-600" role="alert">
     {error}
    </p>
   )}
   {helperText && !error && (
    <p id={`${selectId}-helper`} className="mt-1 text-sm text-gray-500">
     {helperText}
    </p>
   )}
  </div>
 );
};

export default Select;

